"use client";

import React, { useState } from "react";
import ExclamationInCircleIcon from "../_icons/ExclaimationInCircleIcon";
import InfoCircleIcon from "../_icons/infoCircleIcon";
import CloseIcon from "../_icons/closeIcon";
import WarningTriangleIcon from "../_icons/warningTriangleIcon";

export type BannerVariant = "info" | "warning" | "error" | "mutedWarning";

interface BannerProps {
  variant?: BannerVariant;
  title?: string;
  isCloseable?: boolean;
  onClose?: () => void;
  className?: string;
  children: React.ReactNode;
}

const variantStyles: Record<
  BannerVariant,
  { container: string; iconColor: string; icon: React.ReactNode }
> = {
  info: {
    container: "bg-[#EEFBFC] border-[#69D0DD] text-slate-900",
    iconColor: "text-[#286271]",
    icon: <InfoCircleIcon className="w-4 h-4" />,
  },
  warning: {
    container: "bg-amber-50 border-amber-500",
    iconColor: "",
    icon: <WarningTriangleIcon />,
  },
  error: {
    container: "bg-[#FFEDED] border-[#E70000]",
    iconColor: "text-rose-600",
    icon: (
      <div className="w-4 h-4 [&>svg]:w-4 [&>svg]:h-4">
        <ExclamationInCircleIcon />
      </div>
    ),
  },
  mutedWarning: {
    container: "bg-[#F4F4F4] border-transparent",
    iconColor: "",
    icon: <WarningTriangleIcon fillColor="#F4F4F4" />,
  },
};

export default function VariantBanner({
  variant = "info",
  title,
  isCloseable = false,
  onClose,
  className = "",
  children,
}: Readonly<BannerProps>) {
  const [isVisible, setIsVisible] = useState(true);

  if (!isVisible) return null;

  const styles = variantStyles[variant];

  const handleClose = () => {
    setIsVisible(false);
    if (onClose) onClose();
  };

  return (
    <div
      className={`p-4 rounded-lg border flex gap-4 items-start relative ${styles.container} ${className}`}
      role="alert"
    >
      {/* Variant Icon */}
      <div className={`flex-shrink-0 ${styles.iconColor}`}>{styles.icon}</div>

      {/* Content Area */}
      <div className="flex-1">
        {title && (
          <h3 className="text-lg leading-6 mb-1.5 font-medium text-black">
            {title}
          </h3>
        )}
        <div className="text-base font-normal text-black">{children}</div>
      </div>

      {/* Close Button */}
      {isCloseable && (
        <button onClick={handleClose} type="button" aria-label="Close alert">
          <CloseIcon />
        </button>
      )}
    </div>
  );
}
